import React, { useEffect, useState } from "react";

import { settlementService } from "api/services/settlement.service";
import ContentHeader from "components/Layout/ContentHeader";
import Button from "components/UI/Button";
import { Input, Select } from "components/UI/Input";
import Modal from "components/UI/Modal";
import Table, { Column } from "components/UI/Table";

import type { PaymentSetting } from "utils/type/settlement";
import "pages/AdminPage.scss";
import "./SettlementSettings.scss";

const SettlementSettings: React.FC = () => {
  const [settings, setSettings] = useState<PaymentSetting[]>([]);
  const [loading, setLoading] = useState(false);

  // 생성/수정 모달
  const [formModal, setFormModal] = useState<{
    open: boolean;
    editing: PaymentSetting | null;
  }>({ open: false, editing: null });
  const [form, setForm] = useState({
    instructor: "",
    lesson_product: "",
    day_order: "",
    is_theory: false,
    payment_type: "PERCENTAGE" as string,
    percentage: "",
    per_person_amount: "",
    fixed_amount: "",
    tiered_rates: "",
  });
  const [saving, setSaving] = useState(false);

  // 삭제 모달
  const [deleteModal, setDeleteModal] = useState<{
    open: boolean;
    setting: PaymentSetting | null;
  }>({ open: false, setting: null });

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const res = await settlementService.listSettings();
      setSettings(Array.isArray(res.data) ? res.data : (res.data.results ?? []));
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  const openForm = (setting?: PaymentSetting) => {
    if (setting) {
      setForm({
        instructor: setting.instructor ? String(setting.instructor) : "",
        lesson_product: String(setting.lesson_product),
        day_order: setting.day_order ? String(setting.day_order) : "",
        is_theory: setting.is_theory,
        payment_type: setting.payment_type,
        percentage: setting.percentage ? String(setting.percentage) : "",
        per_person_amount: setting.per_person_amount ? String(setting.per_person_amount) : "",
        fixed_amount: setting.fixed_amount ? String(setting.fixed_amount) : "",
        tiered_rates: setting.tiered_rates ? JSON.stringify(setting.tiered_rates) : "",
      });
      setFormModal({ open: true, editing: setting });
    } else {
      setForm({
        instructor: "",
        lesson_product: "",
        day_order: "",
        is_theory: false,
        payment_type: "PERCENTAGE",
        percentage: "",
        per_person_amount: "",
        fixed_amount: "",
        tiered_rates: "",
      });
      setFormModal({ open: true, editing: null });
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload: Record<string, unknown> = {
        lesson_product: Number(form.lesson_product),
        payment_type: form.payment_type,
        is_theory: form.is_theory,
      };
      if (form.instructor) payload.instructor = Number(form.instructor);
      if (form.day_order) payload.day_order = Number(form.day_order);
      if (form.payment_type === "PERCENTAGE" && form.percentage) {
        payload.percentage = Number(form.percentage);
      }
      if (form.payment_type === "PER_PERSON" && form.per_person_amount) {
        payload.per_person_amount = Number(form.per_person_amount);
      }
      if (form.payment_type === "FIXED" && form.fixed_amount) {
        payload.fixed_amount = Number(form.fixed_amount);
      }
      if (form.payment_type === "TIERED" && form.tiered_rates) {
        payload.tiered_rates = JSON.parse(form.tiered_rates);
      }

      if (formModal.editing) {
        await settlementService.updateSetting(formModal.editing.id, payload);
      } else {
        await settlementService.createSetting(payload);
      }
      setFormModal({ open: false, editing: null });
      fetchSettings();
    } catch {
      // ignore
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteModal.setting) return;
    try {
      await settlementService.deleteSetting(deleteModal.setting.id);
      setDeleteModal({ open: false, setting: null });
      fetchSettings();
    } catch {
      // ignore
    }
  };

  const getAmountDisplay = (setting: PaymentSetting) => {
    switch (setting.payment_type) {
      case "PERCENTAGE":
        return `${setting.percentage}%`;
      case "PER_PERSON":
        return `${Number(setting.per_person_amount).toLocaleString()}원/인`;
      case "FIXED":
        return `${Number(setting.fixed_amount).toLocaleString()}원`;
      case "TIERED":
        return "구간별";
      default:
        return "-";
    }
  };

  const columns: Column<PaymentSetting>[] = [
    {
      key: "instructor_name",
      title: "강사",
      width: "100px",
      render: (v, row) => (row.instructor ? String(v) : "기본 설정"),
    },
    { key: "lesson_product_name", title: "강습 상품", width: "180px" },
    {
      key: "day_order",
      title: "Day",
      width: "70px",
      render: (v, row) => (row.is_theory ? "이론" : v ? `Day ${v}` : "-"),
    },
    {
      key: "payment_type_display",
      title: "정산 방식",
      width: "100px",
    },
    {
      key: "percentage",
      title: "금액/비율",
      width: "120px",
      render: (_, row) => getAmountDisplay(row),
    },
    {
      key: "actions",
      title: "관리",
      width: "120px",
      render: (_, row) => (
        <div className="settings-actions">
          <Button size="sm" variant="ghost" onClick={() => openForm(row)}>
            수정
          </Button>
          <Button size="sm" variant="danger" onClick={() => setDeleteModal({ open: true, setting: row })}>
            삭제
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="admin-page">
      <ContentHeader
        title="정산 설정"
        description="강사별/강습별 정산 설정을 관리합니다."
        actions={[
          <Button key="add" variant="primary" onClick={() => openForm()}>
            설정 추가
          </Button>,
        ]}
      />

      <div className="admin-page__card">{loading ? <div className="admin-page__loading">불러오는 중...</div> : <Table columns={columns} data={settings} rowKey="id" />}</div>

      {/* 생성/수정 모달 */}
      <Modal
        open={formModal.open}
        title={formModal.editing ? "정산 설정 수정" : "정산 설정 추가"}
        width="500px"
        onClose={() => setFormModal({ open: false, editing: null })}
        footer={
          <div className="modal-footer-actions">
            <Button variant="secondary" onClick={() => setFormModal({ open: false, editing: null })}>
              취소
            </Button>
            <Button variant="primary" onClick={handleSave} disabled={saving}>
              {saving ? "저장 중..." : "저장"}
            </Button>
          </div>
        }
      >
        <div className="form-fields">
          <Input label="강사 ID (비우면 기본 설정)" value={form.instructor} onChange={(e) => setForm((p) => ({ ...p, instructor: e.target.value }))} placeholder="강사 ID (선택)" />
          <Input label="강습 상품 ID" value={form.lesson_product} onChange={(e) => setForm((p) => ({ ...p, lesson_product: e.target.value }))} placeholder="강습 상품 ID" />
          <Input label="Day 순서 (비우면 전체)" value={form.day_order} onChange={(e) => setForm((p) => ({ ...p, day_order: e.target.value }))} placeholder="Day 순서" />
          <label className="checkbox-label">
            <input type="checkbox" checked={form.is_theory} onChange={(e) => setForm((p) => ({ ...p, is_theory: e.target.checked }))} />
            이론 수업
          </label>
          <Select
            label="정산 방식"
            value={form.payment_type}
            onChange={(e) => setForm((p) => ({ ...p, payment_type: e.target.value }))}
            options={[
              { value: "PERCENTAGE", label: "퍼센티지" },
              { value: "PER_PERSON", label: "1인당" },
              { value: "FIXED", label: "고정금액" },
              { value: "TIERED", label: "구간별 차등" },
            ]}
          />
          {form.payment_type === "PERCENTAGE" && <Input label="퍼센티지 (%)" type="number" value={form.percentage} onChange={(e) => setForm((p) => ({ ...p, percentage: e.target.value }))} placeholder="예: 30" />}
          {form.payment_type === "PER_PERSON" && <Input label="1인당 금액 (원)" type="number" value={form.per_person_amount} onChange={(e) => setForm((p) => ({ ...p, per_person_amount: e.target.value }))} placeholder="예: 50000" />}
          {form.payment_type === "FIXED" && <Input label="고정 금액 (원)" type="number" value={form.fixed_amount} onChange={(e) => setForm((p) => ({ ...p, fixed_amount: e.target.value }))} placeholder="예: 200000" />}
          {form.payment_type === "TIERED" && <Input label="구간별 금액 (JSON)" value={form.tiered_rates} onChange={(e) => setForm((p) => ({ ...p, tiered_rates: e.target.value }))} placeholder='예: {"1": 50000, "2": 90000, "3+": 120000}' />}
        </div>
      </Modal>

      {/* 삭제 확인 모달 */}
      <Modal
        open={deleteModal.open}
        title="정산 설정 삭제"
        width="440px"
        onClose={() => setDeleteModal({ open: false, setting: null })}
        footer={
          <div className="modal-footer-actions">
            <Button variant="secondary" onClick={() => setDeleteModal({ open: false, setting: null })}>
              취소
            </Button>
            <Button variant="danger" onClick={handleDelete}>
              삭제
            </Button>
          </div>
        }
      >
        <p className="confirm-text">이 정산 설정을 삭제하시겠습니까? 이 작업은 되돌릴 수 없습니다.</p>
      </Modal>
    </div>
  );
};

export default SettlementSettings;
